import $ from 'jquery';
import { buildSummaryLine } from './capture';
import { trackClientValidationBlocked, trackModalOpen, trackSendFailure, trackSendSuccess } from './analytics';
import { attachPhoneFormatting, format as formatPhone, isAcceptable as isAcceptablePhone } from './phone';

/*
 * Issue #227: the Save Results modal.
 *
 * Bootstrap 4 markup, matching the site-standard pattern used by the builder-partner modal in
 * Views/AboutUs/Builders.cshtml. Injected once per page rather than emitted per calculator, because the
 * calculator UI is built client-side and there is no server-rendered place to put it.
 *
 * All copy here is verbatim from the approved v2 mockup, including the three inline error messages and
 * the success state.
 */

const MODAL_ID = 'cmg-save-results-modal';

// Same rule as the mockup. The server applies a stricter one (it also rejects "a@b..com"), and returns
// per-field errors that this modal renders, so a stricter server is safe.
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/*
 * Issue #295, AC-16: the modal's passive consent microcopy is GONE, replaced by the required TCPA
 * checkbox and the brand-specific disclosure beneath it.
 *
 * What was here: two strings beginning "By sending, you agree that CMG Home Loans...", one for corp
 * and one naming the page owner. Both are removed rather than kept alongside the new block -- the old
 * text asserted consent implied by sending while the new text requires an affirmative tick, and a form
 * that says two different things about the same act is exactly the ambiguity affirmative consent
 * exists to remove. The modal now carries ONE consent statement.
 *
 * Note the old copy hard-coded "CMG Home Loans" on all brands. The replacement does not: the wording
 * is composed server-side per brand by CalculatorConsentDisclosure.cs and arrives as
 * context.consentDisclosureHtml. No disclosure copy lives in this file, which is what keeps a
 * Compliance revision a single edit.
 */

/** Inline error copy for the new fields. Exact strings from AC-14 and AC-17 (assertions A2 and A4). */
const PHONE_INCOMPLETE_MESSAGE = 'Enter a 10-digit phone number';
const CONSENT_REQUIRED_MESSAGE = 'Please check this box to continue';

/**
 * Field key -> the control that gets .invalid / aria-invalid.
 *
 * A lookup rather than the ternary chain that was here before issue #295. That chain ended in a
 * bare `: 'email'`, so ANY key it did not know about marked the Email input invalid -- and this is
 * also the function that renders the server's per-field errors, so a server-side `field: "phone"`
 * rejection would have highlighted the wrong control. An unknown key now matches nothing, which is
 * visibly nothing rather than visibly wrong.
 */
const FIELD_SELECTORS = {
    firstName: '[data-save-results-first]',
    lastName: '[data-save-results-last]',
    phone: '[data-save-results-phone]',
    email: '[data-save-results-email]',
    consent: '[data-save-results-consent-check]'
};

/** The keys validate() clears and sets on every attempt. Order matches the field order. */
const VALIDATED_FIELDS = ['firstName', 'lastName', 'phone', 'email', 'consent'];

function template(context) {
    const siteKey = context.recaptchaSiteKey || '';

    return ''
        + '<div class="modal fade cmg-save-results" id="' + MODAL_ID + '" tabindex="-1" role="dialog"'
        + '     aria-modal="true" aria-labelledby="' + MODAL_ID + '-title">'
        + '  <div class="modal-dialog modal-dialog-centered" role="document">'
        + '    <div class="modal-content">'
        + '      <div class="modal-header">'
        + '        <button type="button" class="close" data-dismiss="modal" aria-label="Close">&times;</button>'
        + '      </div>'
        + '      <div class="modal-body">'
        + '        <div class="save-results-form" data-save-results-form>'
        + '          <h4 id="' + MODAL_ID + '-title">Save Results</h4>'
        + '          <p class="save-results-sub">We&rsquo;ll send this scenario and its results to the email address you enter below.</p>'
        + '          <div class="save-results-summary" data-save-results-summary></div>'
        + '          <form novalidate>'
        // Each error <p> is a role="alert" live region tied to its input via aria-describedby, and
        // setFieldError toggles aria-invalid alongside the visual classes -- without these the only
        // invalid-state signal is a red border, which a screen reader never announces (PR #269 review).
        + '            <div class="save-results-field">'
        + '              <label for="' + MODAL_ID + '-first">First Name</label>'
        + '              <input type="text" id="' + MODAL_ID + '-first" autocomplete="given-name"'
        + '                     aria-describedby="' + MODAL_ID + '-first-error" data-save-results-first />'
        + '              <p class="save-results-error" id="' + MODAL_ID + '-first-error" role="alert"'
        + '                 data-save-results-error="firstName">Please enter a first name.</p>'
        + '            </div>'
        + '            <div class="save-results-field">'
        + '              <label for="' + MODAL_ID + '-last">Last Name</label>'
        + '              <input type="text" id="' + MODAL_ID + '-last" autocomplete="family-name"'
        + '                     aria-describedby="' + MODAL_ID + '-last-error" data-save-results-last />'
        + '              <p class="save-results-error" id="' + MODAL_ID + '-last-error" role="alert"'
        + '                 data-save-results-error="lastName">Please enter a last name.</p>'
        + '            </div>'
        // Issue #295, AC-10: directly below Last Name and directly above Email Address, so the field
        // order is First -> Last -> Phone -> Email. The label carries "(optional)" explicitly --
        // this is the modal's first optional field, so there was no existing convention to copy.
        // inputmode="numeric" rather than "tel" brings up a digits-only keypad on a touch device;
        // the tel keypad offers + * #, which AC-11 rejects anyway.
        //
        // *** Deliberately NO maxlength. *** The browser truncates a PASTE to maxLength before any
        // JS sees it, so a 14-char cap silently corrupts the exact case AC-11 calls out: pasting
        // "+1-973-534-7079" (15 chars) arrives as "+1-973-534-707", whose ten digits are
        // "1973534707" -- stripCountryCode only fires at exactly eleven, so the leading 1 is kept
        // and the field accepts "(197)-353-4707". That passes client AND server validation and
        // hands the loan officer a dialable number the visitor never entered; the server cannot
        // recover, because the mangling happened before the request. The ten-digit cap is enforced
        // in normalize() / CalculatorPhoneNumber.Normalize, which see the whole pasted value.
        + '            <div class="save-results-field">'
        + '              <label for="' + MODAL_ID + '-phone">Phone Number (optional)</label>'
        + '              <input type="tel" id="' + MODAL_ID + '-phone" inputmode="numeric" autocomplete="tel"'
        + '                     placeholder="(973)-534-7079"'
        + '                     aria-describedby="' + MODAL_ID + '-phone-error" data-save-results-phone />'
        + '              <p class="save-results-error" id="' + MODAL_ID + '-phone-error" role="alert"'
        + '                 data-save-results-error="phone">' + PHONE_INCOMPLETE_MESSAGE + '</p>'
        + '            </div>'
        + '            <div class="save-results-field">'
        + '              <label for="' + MODAL_ID + '-email">Email Address</label>'
        + '              <input type="email" id="' + MODAL_ID + '-email" autocomplete="email" placeholder="name@example.com"'
        + '                     aria-describedby="' + MODAL_ID + '-email-error" data-save-results-email />'
        + '              <p class="save-results-error" id="' + MODAL_ID + '-email-error" role="alert"'
        + '                 data-save-results-error="email">Please enter a valid email address.</p>'
        + '            </div>'
        // Deliberately NOT class="g-recaptcha": api.js only auto-renders that class during its scan
        // at script load, and this modal is injected after it. The widget is rendered explicitly in
        // mountRecaptcha(), which also avoids a double-render race if api.js loads late.
        + (siteKey
            ? '            <div class="save-results-captcha" data-save-results-captcha></div>'
            : '')
        // Issue #295, AC-16/AC-17: the required consent checkbox with the disclosure beneath it,
        // sitting below the reCAPTCHA and above Send Results -- which is where the microcopy this
        // replaces already sat, so nothing moves. Unticked by default.
        //
        // The label wraps only the checkbox and its short visible text; the disclosure paragraph is a
        // sibling, not part of the label, because a label containing a dense paragraph with two links
        // makes the whole block a click target that toggles the box when a visitor tries to select
        // text or follow a link.
        + '            <div class="save-results-consent-field">'
        + '              <div class="save-results-consent-row">'
        + '                <input type="checkbox" id="' + MODAL_ID + '-consent"'
        + '                       aria-describedby="' + MODAL_ID + '-consent-error ' + MODAL_ID + '-consent-text"'
        + '                       data-save-results-consent-check />'
        + '                <label for="' + MODAL_ID + '-consent">I agree to the terms below</label>'
        + '              </div>'
        + '              <p class="save-results-error" id="' + MODAL_ID + '-consent-error" role="alert"'
        + '                 data-save-results-error="consent">' + CONSENT_REQUIRED_MESSAGE + '</p>'
        // Populated at open() from context.consentDisclosureHtml, which PageTemplate.cshtml renders
        // server-side. No disclosure copy lives in this file.
        + '              <div class="save-results-consent" id="' + MODAL_ID + '-consent-text"'
        + '                   data-save-results-consent></div>'
        + '            </div>'
        + '            <p class="save-results-error save-results-error--form" role="alert" data-save-results-form-error></p>'
        // Send Results is the primary green action and Cancel is the mockup's "ghost" treatment
        // (transparent, grey, sentence case). button-primary is required here: .button-cmg on its own
        // sets color:#fff with no background-color, so an unmodified button-cmg is white-on-transparent
        // -- invisible against the modal.
        + '            <div class="save-results-actions">'
        + '              <button type="submit" class="button-cmg button-small button-primary" data-save-results-send>Send Results</button>'
        + '              <button type="button" class="save-results-cancel" data-dismiss="modal">Cancel</button>'
        + '            </div>'
        + '          </form>'
        + '        </div>'
        // role="status": a polite live region, so the confirmation is announced when the panel swaps
        // in -- visually the success state is obvious; audibly it was silence.
        + '        <div class="save-results-success" role="status" data-save-results-success>'
        + '          <div class="save-results-success-check">'
        + '            <svg viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="3" stroke-linecap="round"'
        + '                 stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M20 6 9 17l-5-5"/></svg>'
        + '          </div>'
        + '          <h4>Results Sent!</h4>'
        // &nbsp; rather than a plain space: the space before the address was reported missing in the
        // rendered modal, and an entity survives any whitespace collapsing between here and the DOM.
        + '          <p>We emailed this scenario to&nbsp;<b data-save-results-sent-to></b>. Check your inbox in the next few minutes.</p>'
        + '          <div class="save-results-actions">'
        + '            <button type="button" class="button-cmg button-small button-secondary" data-dismiss="modal">Done</button>'
        + '          </div>'
        + '        </div>'
        + '      </div>'
        + '    </div>'
        + '  </div>'
        + '</div>';
}

export default class SaveResultsModal {
    constructor(context) {
        this.context = context;
        this.$modal = null;
        this.capture = null;
        this.submitting = false;
        // The 429 hold deadline (epoch ms) and its explanation. Real state, not just a disabled
        // button: the disabled prop alone was wiped by reset() on Cancel -> reopen, which made the
        // hold decorative (PR #269 review). 0 = no hold.
        this.holdUntilMs = 0;
        this.holdMessage = '';
        // Where to send focus back when the modal closes.
        this.returnFocusTo = null;
        // null = not rendered. 0 is a valid grecaptcha widget id, so every check is against null.
        this.recaptchaWidgetId = null;
    }

    ensureMounted() {
        if (this.$modal && this.$modal.length && $.contains(document.body, this.$modal[0])) {
            return;
        }

        $('#' + MODAL_ID).remove();
        this.$modal = $(template(this.context)).appendTo('body');
        this.recaptchaWidgetId = null;
        this.bind();

        // Issue #295, AC-11 to AC-13. Attached here rather than in bind()'s delegated handlers
        // because the digits-only rejection needs a cancelable beforeinput on the element itself --
        // jQuery's delegated events cannot preventDefault it reliably.
        attachPhoneFormatting(this.$modal.find('[data-save-results-phone]')[0]);
    }

    /**
     * Renders the reCAPTCHA widget into the modal. Explicit, because api.js auto-renders only the
     * .g-recaptcha divs that exist when the script loads, and this modal is injected on first open.
     * Safe to call repeatedly: it no-ops once rendered, and retries on the next open() if api.js had
     * not finished loading yet.
     */
    mountRecaptcha() {
        if (this.recaptchaWidgetId !== null || !this.context.recaptchaSiteKey) {
            return;
        }

        const el = this.$modal.find('[data-save-results-captcha]')[0];

        if (!el || !window.grecaptcha || typeof window.grecaptcha.render !== 'function') {
            return;
        }

        try {
            this.recaptchaWidgetId = window.grecaptcha.render(el, {
                sitekey: this.context.recaptchaSiteKey
            });
        } catch (e) {
            /* api.js in a bad state; recaptchaToken() still returns '' and the server rejects cleanly. */
        }
    }

    bind() {
        const self = this;

        this.$modal.on('submit', 'form', (e) => {
            e.preventDefault();
            self.submit();
        });

        // Clearing the error as soon as the visitor edits the field matches the mockup's behaviour.
        //
        // Issue #295: 'change' joins 'input' because a checkbox does not fire 'input' in every
        // browser, so without it the consent error would stay on screen after the visitor ticked the
        // box (AC-17 requires ticking to clear it). The closest() also widens to the consent wrapper,
        // since the checkbox lives in .save-results-consent-field rather than .save-results-field.
        this.$modal.on('input change', 'input', (e) => {
            const $input = $(e.currentTarget);
            $input.removeClass('invalid').attr('aria-invalid', 'false');
            $input.closest('.save-results-field, .save-results-consent-field')
                .find('.save-results-error').removeClass('show');

            // During a 429 hold the form error IS the explanation for the disabled button --
            // erasing it on the first keystroke leaves a dead button with no reason given.
            if (!self.isHeld()) {
                self.$modal.find('[data-save-results-form-error]').removeClass('show').text('');
            }
        });

        // Bootstrap 4's modal focuses the dialog element itself at the end of its fade transition,
        // discarding and then overriding any focus set synchronously in open() -- so the first-field
        // focus has to ride the shown event (repo precedent: src/js/loan.js).
        this.$modal.on('shown.bs.modal', () => {
            self.$modal.find('[data-save-results-first]').trigger('focus');
        });

        this.$modal.on('hidden.bs.modal', () => {
            if (self.returnFocusTo && document.contains(self.returnFocusTo)) {
                $(self.returnFocusTo).trigger('focus');
            }
            self.returnFocusTo = null;
        });
    }

    isHeld() {
        return Date.now() < this.holdUntilMs;
    }

    open(capture) {
        this.ensureMounted();
        this.mountRecaptcha();
        this.capture = capture;

        const heading = this.context.calculatorDisplayName
            + ' · ' + this.tabDisplayName(capture.activeTab);

        this.$modal.find('[data-save-results-summary]').html(
            '<b></b><div class="save-results-summary-line"><span></span><span></span></div>');
        this.$modal.find('[data-save-results-summary] b').text(heading);
        this.$modal.find('[data-save-results-summary] .save-results-summary-line span').eq(0)
            .text(buildSummaryLine(capture));
        this.$modal.find('[data-save-results-summary] .save-results-summary-line span').eq(1)
            .text(capture.headlineValue || '');

        // Issue #295, AC-16. .html() and not .text(): the disclosure's final sentence carries the two
        // policy anchors. The markup is composed server-side by CalculatorConsentDisclosure, which
        // HTML-encodes the brand name and both hrefs, so the only tags reaching here are the two
        // anchors it emitted itself.
        this.$modal.find('[data-save-results-consent]').html(this.context.consentDisclosureHtml || '');

        // Captured before Bootstrap moves focus into the dialog, so hidden.bs.modal can hand it back
        // to the Save Results button that opened us.
        this.returnFocusTo = document.activeElement;

        this.reset();
        this.$modal.modal('show');

        trackModalOpen(Object.assign({}, this.context, { activeTab: capture.activeTab }));
    }

    tabDisplayName(tab) {
        if (tab === 'advanced') return 'Advanced';
        if (tab === 'amortization') return 'Amortization';
        return 'Basic';
    }

    reset() {
        this.submitting = false;
        this.$modal.find('[data-save-results-form]').removeClass('hide').show();
        this.$modal.find('[data-save-results-success]').removeClass('show').hide();
        this.$modal.find('.save-results-error').removeClass('show');
        this.$modal.find('input').removeClass('invalid').attr('aria-invalid', 'false');

        // Issue #295, AC-16: the checkbox is unticked on every open. Consent is per-submission, so a
        // previous session's tick must not be inherited by the next scenario -- reset() runs from
        // open(), so this is what guarantees "appears unticked by default" on a reopen too.
        this.$modal.find('[data-save-results-consent-check]').prop('checked', false);

        // A live 429 hold survives reset() -- and therefore Cancel -> reopen, which used to
        // re-enable the button immediately and make the hold decorative (PR #269 review).
        if (this.isHeld()) {
            this.applyRateLimitHold();
            return;
        }

        this.$modal.find('[data-save-results-send]').prop('disabled', false).text('Send Results');
    }

    setFieldError(field, show) {
        this.$modal.find('[data-save-results-error="' + field + '"]').toggleClass('show', !!show);
        this.$modal.find(FIELD_SELECTORS[field] || '')
            .toggleClass('invalid', !!show)
            .attr('aria-invalid', show ? 'true' : 'false');
    }

    validate(values) {
        const invalid = [];

        if (!values.firstName) invalid.push('firstName');
        if (!values.lastName) invalid.push('lastName');
        // AC-14: blank is valid; 1-9 digits is not. The server applies the same rule
        // (CalculatorPhoneNumber.Classify) and is the authority.
        if (!isAcceptablePhone(values.phone)) invalid.push('phone');
        if (!EMAIL_PATTERN.test(values.email)) invalid.push('email');
        // AC-17: independent of the phone field. A blank phone never exempts the visitor from
        // consenting, and consenting never makes the phone required.
        if (!values.consentGiven) invalid.push('consent');

        VALIDATED_FIELDS.forEach((field) => {
            this.setFieldError(field, invalid.indexOf(field) !== -1);
        });

        return invalid;
    }

    readValues() {
        return {
            firstName: (this.$modal.find('[data-save-results-first]').val() || '').trim(),
            lastName: (this.$modal.find('[data-save-results-last]').val() || '').trim(),
            // Normalised before it leaves the modal so what is validated, sent, stored and pushed is
            // one shape (AC-11, AC-12). The server re-derives it regardless.
            phone: formatPhone(this.$modal.find('[data-save-results-phone]').val() || ''),
            email: (this.$modal.find('[data-save-results-email]').val() || '').trim(),
            consentGiven: this.$modal.find('[data-save-results-consent-check]').prop('checked') === true
        };
    }

    recaptchaToken() {
        // grecaptcha may be absent if the script has not loaded, or the widget may not have rendered.
        // The server rejects a missing token (AC-12), so sending an empty one produces a clean,
        // non-technical error rather than a silent no-op. Scoped to our widget id so a second
        // reCAPTCHA on the page (e.g. the team contact form) is never read by mistake.
        if (this.recaptchaWidgetId === null) {
            return '';
        }

        try {
            return window.grecaptcha.getResponse(this.recaptchaWidgetId) || '';
        } catch (e) {
            return '';
        }
    }

    showFormError(message) {
        this.$modal.find('[data-save-results-form-error]').text(message).addClass('show');
    }

    submit() {
        // The hold check closes every submission path -- Enter key, programmatic submit, a re-enabled
        // button from a stale timer -- not just the disabled prop on the button.
        if (this.submitting || this.isHeld()) return;

        const values = this.readValues();
        const invalid = this.validate(values);

        if (invalid.length) {
            trackClientValidationBlocked(
                Object.assign({}, this.context, { activeTab: this.capture.activeTab }),
                invalid);
            return;
        }

        // The widget rendered but the box is unchecked: say so here rather than posting. A server
        // round trip would return the same message and, because recaptcha failures count against the
        // per-IP allowance (AC-12), would also spend rate-limit budget on an honest mistake.
        if (this.recaptchaWidgetId !== null && !this.recaptchaToken()) {
            this.showFormError('Please confirm you\'re not a robot.');
            trackClientValidationBlocked(
                Object.assign({}, this.context, { activeTab: this.capture.activeTab }),
                ['recaptcha']);
            return;
        }

        this.submitting = true;
        const $send = this.$modal.find('[data-save-results-send]');
        $send.prop('disabled', true).text('Sending…');

        const payload = Object.assign({}, this.capture, {
            calculatorName: this.context.calculatorDisplayName,
            firstName: values.firstName,
            lastName: values.lastName,
            // Issue #295. Sent as the formatted value, matching what gets stored and pushed
            // (AC-11/AC-15); the server re-derives it either way. Only the FACT of consent is sent
            // -- the disclosure wording is recomposed server-side for the AC-18 record, never taken
            // from the body, so a crafted POST cannot record a disclosure nobody was shown.
            phone: values.phone,
            email: values.email,
            consentGiven: values.consentGiven,
            recaptchaToken: this.recaptchaToken()
        });

        const self = this;

        $.ajax({
            url: this.context.saveResultsUrl,
            type: 'POST',
            contentType: 'application/json',
            dataType: 'json',
            data: JSON.stringify(payload)
        }).done((response) => {
            if (response && response.success) {
                self.showSuccess(response.sentTo || values.email);
                trackSendSuccess(Object.assign({}, self.context, { activeTab: self.capture.activeTab }));
                return;
            }

            // AC-10: an email failure gets a non-technical retry message, not a success state.
            self.reset();
            self.showFormError((response && response.message)
                || 'We couldn\'t save your results. Please try again.');
            trackSendFailure(
                Object.assign({}, self.context, { activeTab: self.capture.activeTab }),
                (response && response.code) || 'UNKNOWN');
        }).fail((xhr) => {
            // A 429 is handled before reset(), because reset() re-enables the Send button. Offering an
            // immediate retry on a rate-limit rejection is exactly the hammering the limit exists to
            // stop, so this branch keeps the button disabled for the server's Retry-After instead.
            if (xhr && xhr.status === 429) {
                self.handleRateLimited(xhr);
                return;
            }

            self.reset();

            let message = 'We couldn\'t save your results. Please try again.';
            let code = 'HTTP_' + (xhr && xhr.status ? xhr.status : 'ERROR');

            const body = xhr && xhr.responseJSON;

            if (body && body.fieldErrors && body.fieldErrors.length) {
                // AC-2's direct-POST leg: the server validated more strictly than the modal did, so its
                // per-field errors are rendered inline exactly like the client-side ones.
                // The modal only has an error slot for the five fields it renders. The validator can
                // also reject `request`, `calculatorId`, `activeTab`, `results`, `inputs` and
                // `amortizationSummary` -- scenario-shape failures that only a hand-crafted POST or a
                // renamed data-result key can trigger. Those have no slot, so without the fallback
                // below the loop silently rendered nothing, the `else if (body.message)` branch was
                // skipped (fieldErrors is non-empty), and the visitor got a re-enabled Send button
                // with no explanation at all.
                var unslotted = [];

                body.fieldErrors.forEach((fieldError) => {
                    var $slot = self.$modal.find('[data-save-results-error="' + fieldError.field + '"]');

                    if ($slot.length) {
                        $slot.text(fieldError.message).addClass('show');
                        self.setFieldError(fieldError.field, true);
                    } else {
                        unslotted.push(fieldError.message);
                    }
                });

                if (unslotted.length) {
                    self.showFormError(unslotted[0]);
                }

                code = 'VALIDATION_FAILED';
            } else if (body && body.message) {
                message = body.message;
                code = body.code || code;
                self.showFormError(message);
            } else {
                self.showFormError(message);
            }

            trackSendFailure(
                Object.assign({}, self.context, { activeTab: self.capture.activeTab }),
                code);
        }).always(() => {
            self.resetRecaptcha();
        });
    }

    /**
     * The HTTP 429 branch: show wait-copy and hold the Send button for the server's Retry-After.
     *
     * Deliberately distinct from AC-10's retry treatment. AC-10 specifies "We couldn't save your
     * results. Please try again." with a Retry action, which is right for a genuine dispatch failure --
     * but on a rate-limit rejection that message invites the visitor to hammer the endpoint. AC-10's
     * path stays reserved for the case where retrying actually helps.
     */
    handleRateLimited(xhr) {
        const body = (xhr && xhr.responseJSON) || {};

        // Prefer the header (the HTTP-level contract), fall back to the body copy of it. The body carries
        // it too because a value the client has to go looking for in the headers is a value a future edit
        // will quietly drop.
        let seconds = 0;
        try {
            seconds = parseInt(xhr.getResponseHeader('Retry-After'), 10) || 0;
        } catch (e) {
            /* Header unavailable (e.g. a cross-origin edge); the body value below covers it. */
        }
        if (!seconds) {
            seconds = body.retryAfterSeconds || 60;
        }

        this.submitting = false;
        // Real hold state, checked by submit() and preserved by reset(). Capped at 5 minutes so a
        // pathological Retry-After cannot brick the button for the session.
        this.holdUntilMs = Date.now() + Math.min(seconds, 300) * 1000;
        this.holdMessage = body.message
            || 'You\'ve sent several sets of results recently. Please wait a few minutes and try again.';

        this.$modal.find('[data-save-results-form]').removeClass('hide').show();
        this.$modal.find('[data-save-results-success]').removeClass('show').hide();
        this.applyRateLimitHold();

        trackSendFailure(
            Object.assign({}, this.context, { activeTab: this.capture.activeTab }),
            body.code || 'RATE_LIMITED');
    }

    /**
     * Renders the current hold: wait-copy in the form error, Send disabled, and a timer to re-enable
     * once the window passes -- the visitor may legitimately still want to send, just not yet.
     * Called from handleRateLimited and again from reset() when a held modal is reopened.
     */
    applyRateLimitHold() {
        this.showFormError(this.holdMessage);

        const $send = this.$modal.find('[data-save-results-send]');
        $send.prop('disabled', true).text('Please wait…');

        const self = this;
        window.setTimeout(() => {
            // Guard against a later submit having taken over the button, and against a stale timer
            // from an earlier, shorter hold firing inside a newer one.
            if (!self.submitting && !self.isHeld()) {
                $send.prop('disabled', false).text('Send Results');
                self.$modal.find('[data-save-results-form-error]').removeClass('show').text('');
            }
        }, Math.max(this.holdUntilMs - Date.now(), 0) + 50);
    }

    resetRecaptcha() {
        if (this.recaptchaWidgetId === null) {
            return;
        }

        try {
            window.grecaptcha.reset(this.recaptchaWidgetId);
        } catch (e) {
            /* A stale token simply fails verification on the next attempt. */
        }
    }

    showSuccess(sentTo) {
        // AC-13: the success state names the destination address explicitly. The panel is shown
        // BEFORE the address text is written into it: the container is a role="status" live region,
        // and screen readers announce content changes inside a visible live region far more reliably
        // than the un-hiding of one that was already populated.
        this.$modal.find('[data-save-results-form]').hide();
        this.$modal.find('[data-save-results-success]').addClass('show').show();
        this.$modal.find('[data-save-results-sent-to]').text(sentTo);
        this.submitting = false;
    }
}
